import {toast} from 'react-toastify';
import {StoreOrderInfoResponseType} from '../../types/TypesUserOrders';

export const scrollToTop = (behavior?: ScrollBehavior | undefined) => {
	window.scrollTo({
		top: 0,
		behavior: behavior ? behavior : 'auto', // Для плавной прокрутки
	});
};
export const scrollToProducts = (
	productsCount: number,
	behavior: ScrollBehavior = 'smooth',
) => {
	const footer = document.querySelector('footer');

	if (productsCount > 5) {
		// багато продуктів → скролимо до початку списку
	} else if (footer) {
		// мало продуктів → скролимо так, щоб футер не перекривав
		const footerTop = footer.getBoundingClientRect().top + window.scrollY;
		const viewportHeight = window.innerHeight;

		// скролимо трохи вище футера
		window.scrollTo({
			top: footerTop - viewportHeight + 50, // 50px запас
			behavior,
		});
	} else {
		// запасний варіант
		window.scrollTo({top: 0, behavior});
	}
};
export const submitFilterFieldCleaner = <T extends Record<string, any>>(
	values: T,
): Partial<T> => {
	return Object.fromEntries(
		Object.entries(values).filter(
			([_, value]) => value !== '' && value !== null && value !== undefined,
		),
	) as Partial<T>;
};

type Params<T> = {
	apiCall: () => Promise<{data?: T; error?: any}>;
	onSuccess?: (data: T) => void;
	onError?: (data: string) => void;
};

type ParamsWithFullError<T> = {
	apiCall: () => Promise<{data?: T; error?: any}>;
	onSuccess?: (data: T) => void;
	onError?: (error: any) => void;
	onValidationError?: (fieldErrors: Record<string, string>) => void;
};

export async function handleApiCall<T>({
	apiCall,
	onSuccess,
	onError,
}: Params<T>): Promise<void> {
	try {
		const result = await apiCall();

		if ('error' in result && result.error) {
			const err = result.error;
			const msg = err?.data?.message?.textmsg || 'Щось пішло не так';
			toast.error(msg);
			onError && onError(msg);
			return;
		}

		if (result.data) {
			onSuccess?.(result.data);
		}
	} catch (err: any) {
		const msg =
			typeof err === 'object' && err?.data?.message?.textmsg
				? err.data.message.textmsg
				: 'Фатальна помилка';
		toast.error(msg);
	}
}

export async function handleApiCallWithFullError<T>({
	apiCall,
	onSuccess,
	onError,
	onValidationError,
}: ParamsWithFullError<T>): Promise<void> {
	try {
		const result = await apiCall();

		if ('error' in result && result.error) {
			const err = result.error;

			// Check for validation errors first
			if (err?.data?.errors) {
				const apiErrors = err.data.errors;
				const formattedErrors: Record<string, string> = {};

				// Convert array format to single string for each field
				Object.keys(apiErrors).forEach((field) => {
					if (Array.isArray(apiErrors[field]) && apiErrors[field].length > 0) {
						formattedErrors[field] = apiErrors[field][0]; // Take first error message
					}
				});

				// Call validation error handler if provided
				if (onValidationError) {
					onValidationError(formattedErrors);
				} else {
					// Fallback to general error handler
					onError && onError(err);
				}
				return;
			}

			// Handle general errors
			const msg =
				err?.data?.message?.textmsg ||
				err?.data?.message ||
				'Щось пішло не так';
			toast.error(msg);
			onError && onError(err);
			return;
		}

		if (result.data) {
			onSuccess?.(result.data);
		}
	} catch (err: any) {
		const msg =
			typeof err === 'object' && err?.data?.message?.textmsg
				? err.data.message.textmsg
				: 'Фатальна помилка';
		toast.error(msg);
		onError && onError(err);
	}
}

export function formatNumber(
	value: string | number,
	removeFraction = false,
): string {
	let num: number;

	if (typeof value === 'number') {
		num = value;
	} else {
		num = Number(value.replace(',', '.').replace(/\s+/g, ''));
	}

	if (isNaN(num)) return String(value);

	// округляем до 2 знаков после запятой
	num = Math.round(num * 100) / 100;

	if (removeFraction) {
		num = Math.round(num); // убираем дробную часть
	}

	const [intPart, fracPart] = num.toString().split('.');

	const formattedInt = Number(intPart)
		.toLocaleString('uk-UA')
		.replace(/\u00A0/g, ' ');

	if (!removeFraction && fracPart && fracPart !== '00') {
		return `${formattedInt},${fracPart.padEnd(2, '0')}`;
	}

	return formattedInt;
}

export function formatWeight(value: string | number): string {
	let num: number;

	if (typeof value === 'number') {
		num = value;
	} else {
		num = Number(value.replace(',', '.').replace(/\s+/g, ''));
	}

	if (isNaN(num)) return '0,0';

	// округляем до 1 знака после запятой
	num = Math.round(num * 10) / 10;

	const [intPart, fracPart] = num.toString().split('.');

	const formattedInt = Number(intPart)
		.toLocaleString('uk-UA')
		.replace(/\u00A0/g, ' ');

	if (fracPart) {
		return `${formattedInt},${fracPart}`;
	}

	return `${formattedInt},0`;
}

export function adaptStoreOrderToPopupStatus(
	storeOrder: StoreOrderInfoResponseType,
) {
	return {
		id: storeOrder.order_id,
		order_date: storeOrder.price_date,
		create_date: storeOrder.create_date,
		points: 0, // не используется в попапе
		money: 0, // не используется в попапе
		delivery_type: storeOrder.delivery_type,
		status: storeOrder.status,
		post_id: storeOrder.post_id,
		delivery_date: storeOrder.delivery_date,
		comment: storeOrder.comment || '',
		initial_id: 0, // не используется в попапе
		ware_id: storeOrder.ware_id,
		ware_name: storeOrder.ware_name,
		type: storeOrder.type,
		products: [], // не используется в попапе
		currency: 'грн', // по умолчанию
		sum_points: 0, // не используется в попапе
		sum_money: 0, // не используется в попапе
		// Дополнительные поля для PopupStatus
		initial_name: storeOrder.recipient_name,
		contact_name: storeOrder.recipient_name,
		contact_phone: storeOrder.recipient_phone,
		service_type: storeOrder.delivery_method,
		address_name: `${storeOrder.delivery_country} ${storeOrder.delivery_city} ${storeOrder.delivery_address}`,
	};
}
export const formatCityNameForNovaPoshta = (
	cityName: string,
	serviceType: string | null | undefined,
): string => {
	// Начинаем с исходного названия города
	let formattedCityName = cityName.trim();

	// 1. Проверяем, относится ли служба доставки к Новой Почте
	const isNovaPoshta =
		serviceType?.toLowerCase().includes('нова') ||
		serviceType?.toLowerCase().includes('nova');

	if (isNovaPoshta) {
		// 2. Проверяем, отсутствует ли уже префикс населенного пункта (м., с., смт.)
		const hasPrefix =
			formattedCityName.startsWith('м.') ||
			formattedCityName.startsWith('с.') ||
			formattedCityName.startsWith('смт.');

		if (!hasPrefix) {
			// 3. Добавляем префикс "м."
			formattedCityName = `м.${formattedCityName}`;
		}
	}

	return formattedCityName;
};
